import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/PageHeader';
import './ScreenReader.css';
import usePageSearchIndex from '../hooks/usePageSearchIndex';

const ScreenReader = () => {
  const { isMarathi } = useLanguage();

  const pageContent = {
    marathi: {
      title: 'स्क्रीन रीडर सपोर्ट',
      subtitle: 'स्क्रीन रीडर वापरणाऱ्या अभ्यागतांसाठी मार्गदर्शक तत्त्वे आणि वैशिष्ट्ये',
      sections: [
        {
          heading: 'वेबसाइट एक्सेसिबिलिटी वैशिष्ट्ये',
          points: [
            'साइटवर कीबोर्ड नेव्हिगेशन पूर्णपणे समर्थित आहे.',
            'सर्व परस्परसंवादी घटकांमध्ये वर्णनात्मक ARIA लेबल्स समाविष्ट आहेत.',
            '"मुख्य सामग्रीवर जा" लिंक जलद नेव्हिगेशनसाठी उपलब्ध आहे.'
          ]
        },
        {
          heading: 'शिफारस केलेले स्क्रीन रीडर्स',
          points: [
            'सर्वोत्तम परिणामांसाठी NVDA (विंडोज) नवीनतम रिलीझ वापरते.',
            'JAWS (विंडोज) तुमचा ब्राउझर अपडेट ठेवा.',
            'व्हॉइसओव्हर (मॅकओएस / आयओएस) सफारीसह सर्वोत्तम काम करते.'
          ]
        },
        {
          heading: 'ब्राउझर आणि कॉन्फिगरेशन टिप्स',
          points: [
            '"गुगल क्रोम" किंवा "मायक्रोसॉफ्ट एज" ची नवीनतम आवृत्ती वापरा',
            'जर तुम्हाला स्पष्ट फोकस आउटलाइन हवी असेल तर "हाय-कॉन्ट्रास्ट" मोड सक्षम करा.',
            'आवश्यकतेनुसार फॉन्ट आकार वाढवण्यासाठी "Ctrl/Cmd + +" वापरा.'
          ]
        },
        {
          heading: 'उपयुक्त कीबोर्ड शॉर्टकट',
          points: [
            '"टॅब / शिफ्ट + टॅब" फोकस पुढे किंवा मागे हलवा.',
            '"एंटर / स्पेस" फोकस केलेले बटण किंवा लिंक सक्रिय करा.',
            '"एच / शिफ्ट + एच" पुढील/मागील शीर्षकावर जा (स्क्रीन रीडर शॉर्टकट).'
          ]
        },
        {
          heading: 'विविध स्क्रीन रीडर्सशी संबंधित माहिती',
          paragraph: 'भारताची राष्ट्रीय वेबसाइट भारत सरकारच्या वेबसाइट्ससाठीच्या मार्गदर्शक तत्त्वांचे पूर्णपणे पालन करते. आमचे दृष्टीदोष असलेले अभ्यागत स्क्रीन रीडर्ससारख्या सहाय्यक तंत्रज्ञानाचा वापर करून पोर्टलवर प्रवेश करू शकतात. वेबसाइटची माहिती JAWS, NVDA, SAFA, सुपरनोव्हा आणि विंडो-आयज सारख्या वेगवेगळ्या स्क्रीन रीडर्सद्वारे उपलब्ध आहे.',
          table: [
            { sr: '१', name: 'नॉन-व्हिज्युअल डेस्कटॉप एक्सेस (NVDA)', link: 'http://www.nvda-project.org/', type: 'मोफत' },
            { sr: '२', name: 'सिस्टम एक्सेस टू गो', link: 'http://www.satogo.com/', type: 'मोफत' },
            { sr: '३', name: 'हाल', link: 'http://www.yourdolphin.co.uk/productdetail.asp?id=5', type: 'व्यावसायिक' },
            { sr: '४', name: 'JAWS', link: 'http://www.freedomscientific.com/jaws-hq.asp', type: 'व्यावसायिक' },
            { sr: '५', name: 'सुपरनोव्हा', link: 'http://www.yourdolphin.co.uk/productdetail.asp?id=1', type: 'व्यावसायिक' }
          ]
        }
      ]
    },
    english: {
      title: 'Screen Reader Support',
      subtitle: 'Guidelines and features for visitors using screen readers',
      sections: [
        {
          heading: 'Website Accessibility Features',
          points: [
            'Keyboard navigation is fully supported across the site.',
            'All interactive elements include descriptive ARIA labels.',
            '"Skip to Main Content" link remains available for quick navigation.'
          ]
        },
        {
          heading: 'Recommended Screen Readers',
          points: [
            'NVDA (Windows) use the latest release for best results.',
            'JAWS (Windows) keep your browser updated.',
            'VoiceOver (macOS / iOS) works best with Safari.'
          ]
        },
        {
          heading: 'Browser & Configuration Tips',
          points: [
            'Use the latest version of "Google Chrome" or "Microsoft Edge"',
            'Enable "high-contrast" mode if you need a clearer focus outline.',
            'Use "Ctrl/Cmd + +" to increase font size when required.'
          ]
        },
        {
          heading: 'Helpful Keyboard Shortcuts',
          points: [
            '"Tab / Shift + Tab" Move focus forward or backward.',
            '"Enter / Space" Activate the focused button or link.',
            '"H / Shift + H" Jump to the next/previous heading (screen reader shortcut).'
          ]
        },
        {
          heading: 'Information Related To The Various Screen Readers',
          paragraph: 'The National Website of India fully complies with Guidelines for Indian Government Websites. Our visitors with visual impairments can access the Portal using Assistive Technologies, such as screen readers. The information of the Website is accessible with different screen readers, such as JAWS, NVDA, SAFA, Supernova and Window-Eyes.',
          table: [
            { sr: '1', name: 'Non Visual Desktop Access (NVDA)', link: 'http://www.nvda-project.org/', type: 'Free' },
            { sr: '2', name: 'System Access To Go', link: 'http://www.satogo.com/', type: 'Free' },
            { sr: '3', name: 'Hal', link: 'http://www.yourdolphin.co.uk/productdetail.asp?id=5', type: 'Commercial' },
            { sr: '4', name: 'JAWS', link: 'http://www.freedomscientific.com/jaws-hq.asp', type: 'Commercial' },
            { sr: '5', name: 'Supernova', link: 'http://www.yourdolphin.co.uk/productdetail.asp?id=1', type: 'Commercial' }
          ]
        }
      ]
    }
  };

  const content = isMarathi() ? pageContent.marathi : pageContent.english;

  useEffect(() => {
    // Reserved for future non-search side effects if needed
  }, []);

  // Auto-index Screen Reader support page content for search
  usePageSearchIndex({
    mr: ['स्क्रीन रीडर', 'एक्सेसिबिलिटी', 'कीबोर्ड', 'नेव्हिगेशन'],
    en: ['screen reader', 'accessibility', 'keyboard', 'navigation']
  });

  const pageHeaderData = {
    title: {
      marathi: content.title,
      english: content.title
    },
    subtitle: {
      marathi: content.subtitle,
      english: content.subtitle
    }
  };

  return (
    <div className="screen-reader-page">
      <div className="screen-reader-container">
        <PageHeader 
          title={pageHeaderData.title}
          subtitle={pageHeaderData.subtitle}
        />

        <div className="screen-reader-content" data-read-section="screen-reader-content">
          {content.sections.map((section) => (
            <div key={section.heading} className="screen-reader-section">
              <h3 className="screen-reader-section-title">{section.heading}</h3>

              {section.paragraph && (
                <p className="screen-reader-paragraph">
                  {section.paragraph}
                </p>
              )}

              {section.points && section.points.length > 0 && (
                <ul className="screen-reader-list">
                  {section.points.map((point, idx) => (
                    <li key={idx} className="screen-reader-list-item">
                      <svg 
                        className="screen-reader-icon"
                        width="18" 
                        height="18" 
                        viewBox="0 0 18 18" 
                        fill="none" 
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path 
                          d="M9 0L11.781 6.219L18 9L11.781 11.781L9 18L6.219 11.781L0 9L6.219 6.219L9 0Z" 
                          fill="currentColor"
                        />
                        <path 
                          d="M9 5C8.17 5 7.5 5.67 7.5 6.5C7.5 7.33 8.17 8 9 8C9.83 8 10.5 7.33 10.5 6.5C10.5 5.67 9.83 5 9 5ZM9 10.5C8.45 10.5 8 10.95 8 11.5V12.5C8 13.05 8.45 13.5 9 13.5C9.55 13.5 10 13.05 10 12.5V11.5C10 10.95 9.55 10.5 9 10.5Z" 
                          fill="white"
                        />
                      </svg>
                      {point}
                    </li>
                  ))}
                </ul>
              )}

              {section.table && (
                <div className="screen-reader-table-wrapper">
                  <table className="screen-reader-table">
                    <thead>
                      <tr>
                        <th>{isMarathi() ? 'क्रमांक.' : 'Sr. No.'}</th>
                        <th>{isMarathi() ? 'स्क्रीन रीडर' : 'Screen Reader'}</th>
                        <th>{isMarathi() ? 'वेबसाइट लिंक' : 'Website Link'}</th>
                        <th>{isMarathi() ? 'मोफत/व्यावसायिक' : 'Free/Commercial'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.table.map((row) => (
                        <tr key={row.sr}>
                          <td>{row.sr}</td>
                          <td>{row.name}</td>
                          <td>
                            <a href={row.link} target="_blank" rel="noopener noreferrer">
                              {row.link}
                            </a>
                          </td>
                          <td>{row.type}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ScreenReader;
